export interface CanvasWorkflowNodeType {
  type: string;
  label: string;
}

export interface CanvasWorkflowNode {
  id: string;
  type: string;
  config: Record<string, unknown>;
  position: { x: number; y: number };
}

export interface CanvasWorkflowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  condition?: string;
}

export interface CanvasWorkflowResult {
  nodes: CanvasWorkflowNode[];
  edges: CanvasWorkflowEdge[];
}

function extractJsonBlock(content: string): string {
  const fencedMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/i);
  return fencedMatch?.[1]?.trim() ?? content.trim();
}

function buildFallbackPosition(index: number): { x: number; y: number } {
  return {
    x: 180 + ((index % 3) * 220),
    y: 120 + (Math.floor(index / 3) * 120),
  };
}

export function buildCanvasWorkflowPrompt(allowedNodeTypes: CanvasWorkflowNodeType[]): string {
  return [
    "Generate a workflow as strict JSON with shape {\"nodes\": WorkflowNode[], \"edges\": WorkflowEdge[]}.",
    `Allowed node types only: ${allowedNodeTypes.map((nodeType) => nodeType.type).join(", ")}.`,
    "Every node needs id, type, config, and position.",
    "Every edge needs source and target matching node ids.",
    "Do not return markdown explanations outside the JSON payload.",
    "This output is AI-generated and will be reviewed by a human before saving.",
  ].join(" ");
}

export function parseGeneratedCanvasWorkflow(
  content: string,
  allowedNodeTypes: CanvasWorkflowNodeType[],
): CanvasWorkflowResult {
  const parsed = JSON.parse(extractJsonBlock(content)) as {
    nodes?: Array<Partial<CanvasWorkflowNode>>;
    edges?: Array<Partial<CanvasWorkflowEdge>>;
  };

  if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) {
    throw new Error("Generated workflow did not include nodes and edges arrays.");
  }

  if (parsed.nodes.length === 0) {
    throw new Error("Generated workflow did not include any nodes.");
  }

  if (parsed.nodes.length > 25) {
    throw new Error("Generated workflow returned too many nodes.");
  }

  const allowedTypes = new Set(allowedNodeTypes.map((nodeType) => nodeType.type));
  const seenNodeIds = new Set<string>();
  const nodes = parsed.nodes.map((node, index) => {
    const type = String(node.type ?? "");
    if (!allowedTypes.has(type)) {
      throw new Error(`Unsupported node type returned by AI: ${type || "unknown"}`);
    }

    const id =
      typeof node.id === "string" && node.id.trim().length > 0
        ? node.id
        : `generated-node-${index + 1}`;

    if (seenNodeIds.has(id)) {
      throw new Error("Generated workflow returned duplicate node ids.");
    }
    seenNodeIds.add(id);

    return {
      id,
      type,
      config: typeof node.config === "object" && node.config !== null ? node.config as Record<string, unknown> : {},
      position:
        node.position && typeof node.position.x === "number" && typeof node.position.y === "number"
          ? node.position
          : buildFallbackPosition(index),
    } satisfies CanvasWorkflowNode;
  });

  const nodeIds = new Set(nodes.map((node) => node.id));
  const seenEdgeIds = new Set<string>();
  const edges = parsed.edges.map((edge, index) => {
    const source = String(edge.source ?? "");
    const target = String(edge.target ?? "");

    if (!nodeIds.has(source) || !nodeIds.has(target)) {
      throw new Error("Generated workflow edges referenced unknown nodes.");
    }

    const id =
      typeof edge.id === "string" && edge.id.trim().length > 0
        ? edge.id
        : `generated-edge-${index + 1}`;

    if (seenEdgeIds.has(id)) {
      throw new Error("Generated workflow returned duplicate edge ids.");
    }
    seenEdgeIds.add(id);

    return {
      id,
      source,
      target,
      label: typeof edge.label === "string" ? edge.label : undefined,
      condition: typeof edge.condition === "string" ? edge.condition : undefined,
    } satisfies CanvasWorkflowEdge;
  });

  return { nodes, edges };
}
